import React, { useMemo } from "react";
import { Grid } from "@material-ui/core";
import CardWarning from "../cards/CardWarning";
import { useSettingsState } from "../../context/settings";
import ChartData from "./ChartData";
import { usePermissions } from "../../context/auth";
import config, { buildSoilClimateCharts } from "../../constants/charts";
import Irrimax from "../pages/Analytics/components/Card/components/Irrimax";

import { useDataLoader } from "../../data/DataLoader";
import { useAuthState } from "../../context/auth";

import "./ChartsList.css";

const ChartsList = ({ field, range }) => {
  const {
    chartsSettings: { visibility },
  } = useSettingsState();
  const permissions = usePermissions();

  const charts = useMemo(() => {
    // Expand the static `soil_climate` entry into the legacy chart plus one
    // chart per non-archived rootzone profile so the analytics page can render
    // the original Root Zone chart alongside Top 30cm and Top 60cm profiles.
    // Visibility / order is shared across all rootzone charts
    // (they all key off "soil_climate" in the visibility settings).
    const expanded = Object.keys(config).flatMap((chart) => {
      if (chart === "soil_climate") {
        const variants = buildSoilClimateCharts(field);
        return variants.map((v) => ({
          ...v,
          // Keep the visibility-settings ref keyed under the legacy "soil_climate"
          // so a single toggle hides/shows every profile chart together.
          _visibilityRef: "soil_climate",
        }));
      }
      return [{ ...config[chart], id: chart, _visibilityRef: chart }];
    });
    return expanded
      .filter((chart) => permissions.includes(chart.module_id))
      .map((chart) => {
        const settings = visibility?.filter(
          ({ field_id: id, ref }) =>
            field.id === id && chart._visibilityRef === ref
        );
        const defaultHidden = chart.id === "delta_t" ? 1 : 0;
        const { order, hidden } = settings[0] || {
          order: 999,
          hidden: defaultHidden,
        };
        return { ...chart, ...{ order, hidden } };
      })
      .filter((chart) => !chart.hidden)
      .sort((a, b) => (a.order > b.order ? 1 : -1));
  }, [field, visibility, permissions]);

  const hasIrrimax = field.irrimax?.length;
  const hasCharts = charts?.length || hasIrrimax;

  const chartGroups = charts
    .map((item) => item.group)
    .filter((value, index, self) => self.indexOf(value) === index);
  if (chartGroups.includes("soil_climates")) {
    chartGroups.push("recommendations");
    chartGroups.push("squeezes");
  }
  if (chartGroups.includes("mildew_risk")) {
    chartGroups.push("sprays");
  }

  const user = useAuthState();
  useDataLoader({ user, field, groups: chartGroups });

  return visibility && hasCharts ? (
    <Grid container spacing={0} className="charts-grid">
      {hasIrrimax ? <Irrimax charts={field.irrimax} /> : null}
      {charts.map((chart, i) => (
        <ChartData key={i} chart={chart} range={range} />
      ))}
    </Grid>
  ) : (
    <CardWarning message="No charts found!" />
  );
};

export default ChartsList;
